import type { CityConfig } from "../city-template/types";

export const jolietConfig: CityConfig = {
  city: "Joliet",
  state: "Illinois",
  stateAbbr: "IL",
  slug: "joliet",
  route: "/cities/joliet",
  title: "Emergency Passport Joliet, IL — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Joliet, Illinois — as fast as 24 hours. Fast passport renewal and new passport applications for Joliet, Plainfield, Shorewood, Romeoville, and all Will County residents. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Joliet, IL",
  heroSubtitle: "Joliet travelers — from the logistics and warehousing corridor to Joliet Junior College students to Will County families — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving all of Joliet and the southwest Chicago suburbs.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Historic%20downtown%20Joliet%20Illinois%20along%20the%20Des%20Plaines%20River%20with%20the%20Rialto%20Theatre%20and%20the%20historic%20courthouse%20building%2C%20mix%20of%20red%20brick%20industrial%20architecture%20and%20modern%20structures%2C%20riverfront%20with%20green%20banks%2C%20warm%20afternoon%20sunlight%2C%20southwest%20Chicago%20suburb%20atmosphere%2C%20professional%20city%20photography%20without%20text%20or%20people%2C%20diverse%20urban%20small%20cityscape%20with%20historic%20charm&width=1800&height=700&seq=joliet-hero-001&orientation=landscape",
  heroImageAlt: "Joliet Illinois downtown along the Des Plaines River with Rialto Theatre",
  geoPosition: "41.5250;-88.0817",
  geoPlacename: "Joliet, Illinois",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St Suite 380, Chicago IL 60604",
    distance: "Approximately 45 miles, 45 minutes via I-55",
  },
  nearbyCitiesZips: [
    { name: "Joliet", zips: "60431, 60432, 60433, 60434, 60435, 60436" },
    { name: "Plainfield", zips: "60544, 60585, 60586" },
    { name: "Shorewood", zips: "60404" },
    { name: "Romeoville", zips: "60446, 60486" },
    { name: "Crest Hill", zips: "60403, 60435" },
    { name: "Lockport", zips: "60441" },
    { name: "New Lenox", zips: "60451" },
    { name: "Frankfort", zips: "60423" },
    { name: "Mokena", zips: "60448" },
    { name: "Orland Park", zips: "60462, 60467" },
    { name: "Bolingbrook", zips: "60440, 60490" },
    { name: "Homer Glen", zips: "60491" },
    { name: "Lemont", zips: "60439" },
    { name: "Minooka", zips: "60447" },
    { name: "Channahon", zips: "60410, 60450" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to Chicago Passport Agency — no 45-minute drive required" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-building-line", title: "Will County Hub", desc: "Serving Joliet logistics, Joliet Junior College, and southwest Chicago families" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to downtown Chicago required" },
  ],
  internalLinks: [
    { name: "Expedited passport in Naperville, IL", route: "/naperville" },
    { name: "Emergency passport help in Aurora, IL", route: "/aurora" },
    { name: "Urgent passport services in Rockford, IL", route: "/rockford" },
    { name: "Illinois passport services overview", route: "/illinois" },
  ],
  fpcOfficeAddress: "230 S Dearborn St Suite 380, Chicago IL 60604",
  fpcOfficeNote: "Fast Passport Center serves Joliet, Illinois remotely via secure registered courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (45 minutes north via I-55) — without you making the trip. Joliet residents can ship from any UPS or FedEx in Will County.",
  fpcOfficeDistance: "Serving Joliet, Plainfield, Shorewood, Romeoville, Crest Hill, Lockport, New Lenox, Frankfort, Mokena, Orland Park, Bolingbrook, Homer Glen, Lemont, Minooka, Channahon, and all of Will County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Joliet and the southwest Chicago suburbs. From downtown Joliet along the Des Plaines River to the logistics corridor on the west side, from Joliet Junior College to the residential neighborhoods of the south and east sides — we serve all of Will County and beyond.",
  nearbyCitiesIntro: "We serve Joliet and all nearby communities including Plainfield, Shorewood, Romeoville, Crest Hill, Lockport, New Lenox, Frankfort, Mokena, Orland Park, Bolingbrook, Homer Glen, Lemont, Minooka, and Channahon. We also serve DuPage County, Grundy County, and all southwest Chicago suburban communities.",
  howItWorksIntro: "Most Joliet travelers complete everything online and ship documents overnight. No need to drive 45 minutes to downtown Chicago — our couriers handle the Chicago Passport Agency trip for you.",
  mistakesIntro: "Joliet is a major hub in Will County with significant logistics and warehousing employment, a large community college, and growing suburban families. Here are the most common passport mistakes we see from Will County travelers.",
  testimonialsIntro: "Joliet travelers — from the logistics and warehousing corridor to Joliet Junior College students to Will County families — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Joliet, IL? Here are the most common ones we hear from southwest Chicago suburban travelers.",
  statePageRoute: "/states/illinois",
  statePageName: "Illinois",
  dmvWebsite: "https://www.ilsos.gov/facilities/facilitylist.html",
  dmvLabel: "Illinois Secretary of State — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Passport Agency — hand-delivered", "No drive to downtown Chicago required", "Perfect for logistics and college travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Passport Agency — hand-delivered", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 45 miles to downtown Chicago", "Full day commitment — parking and downtown navigation"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Joliet travelers with immediate departure needs — as fast as 24 hours. Our couriers use the Chicago Passport Agency — 45 minutes away but you never make the trip.",
      bullets: ["24–48 hour turnaround available", "Chicago Passport Agency direct courier", "Will County logistics and college travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Joliet adults with a passport issued within the last 15 years. Skip the post office appointment entirely.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Joliet address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Joliet residents and families — including Joliet Junior College students, new citizens, and Will County families.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Joliet residents who lost their passport before international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Joliet and Will County families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Joliet and Will County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Joliet IL", description: "Urgent passport processing for Joliet Illinois travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Joliet IL", description: "Fast passport renewal for Joliet Illinois residents with a previously-issued passport." },
    { name: "First-Time Passport Joliet IL", description: "New passport application services for Joliet Illinois residents applying for their first passport." },
    { name: "Child Passport Joliet IL", description: "Minor passport services for Joliet Illinois families with children under 16." },
  ],
  testimonials: [
    {
      name: "Elena R.",
      location: "Joliet, IL",
      rating: 5,
      text: "Logistics conference in Rotterdam in 5 days and my passport was expired. Fast Passport Center had my renewed passport in 2 days. I shipped from the UPS Store on Jefferson Street and the whole process was seamless. They saved my business trip.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Mark D.",
      location: "Plainfield, IL",
      rating: 5,
      text: "Family cruise to the Caribbean with 7 days notice. Four passports for our family. Done in 4 days. We shipped from FedEx on Route 30 and everything was perfect. The courier service is worth every penny.",
      service: "Family Passport Bundle",
    },
    {
      name: "Jessica T.",
      location: "Joliet, IL",
      rating: 5,
      text: "Study abroad in Japan with 10 days notice. Fast Passport Center walked me through every step and had my passport in 3 days. As a Joliet Junior College student transferring to a university program, I couldn't afford to miss my departure. They saved my semester.",
      service: "Emergency Passport",
    },
    {
      name: "Anthony B.",
      location: "Shorewood, IL",
      rating: 5,
      text: "Lost my passport the week before our wedding in Cancun. Fast Passport Center replaced it in 48 hours. From Shorewood, driving to Chicago with wedding prep going on was impossible. They saved our honeymoon.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "jol-1",
      name: "Joliet Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 Jefferson St",
      city: "Joliet",
      state: "IL",
      zip: "60432",
      phone: "(815) 726-1640",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Joliet, near the Des Plaines River",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Joliet acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient to downtown Joliet and the historic district.",
      icon: "ri-mail-line",
    },
    {
      id: "jol-2",
      name: "Plainfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15317 S Route 59",
      city: "Plainfield",
      state: "IL",
      zip: "60544",
      phone: "(815) 436-4226",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Plainfield, approximately 8 miles north of Joliet",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Plainfield, Shorewood, and northern Joliet residents.",
      icon: "ri-mail-line",
    },
    {
      id: "jol-3",
      name: "Romeoville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2 N Weber Rd",
      city: "Romeoville",
      state: "IL",
      zip: "60446",
      phone: "(815) 886-4224",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Romeoville, approximately 10 miles north of Joliet",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Romeoville, Crest Hill, and northern Will County. Good alternative for north-side travelers.",
      icon: "ri-mail-line",
    },
    {
      id: "jol-4",
      name: "New Lenox Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2541 W Maple St",
      city: "New Lenox",
      state: "IL",
      zip: "60451",
      phone: "(815) 485-3040",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "New Lenox, approximately 12 miles south of Joliet",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for New Lenox, Frankfort, Mokena, and southern Will County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "jol-5",
      name: "Will County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "302 N Chicago St",
      city: "Joliet",
      state: "IL",
      zip: "60432",
      phone: "(815) 740-4615",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Joliet, Will County Government Building",
      website: "https://www.willcountyillinois.com/",
      websiteLabel: "Will County",
      notes: "Will County Clerk passport acceptance. Extended hours compared to some post office locations. Call to confirm current passport acceptance hours.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 45-mile, 45-minute drive to Chicago via I-55", fpc: "No — ship from Joliet" },
    { feature: "Parking & Navigation", agency: "Downtown Chicago parking costs", fpc: "Ship from your neighborhood" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Photo & Document Help", agency: "No guidance provided", fpc: "Yes — full document review included" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Joliet logistics professionals and college students often discover passport issues just before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo services near Jefferson Street or Route 30 sometimes produce photos that meet ID requirements but fail passport standards. Photos must be exactly 2x2 inches on a white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Will County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking Acceptance Facilities Early Enough",
      description: "Joliet and Plainfield Post Office appointments fill during summer travel season and college break periods. Book 2–3 weeks ahead.",
    },
    {
      icon: "ri-road-map-line",
      title: "Underestimating the Trip to Chicago",
      description: "Many Joliet residents attempt to drive to the Chicago Passport Agency in an emergency. The 45-minute drive plus downtown parking and navigation means a half-day lost — our courier service is faster and more reliable.",
    },
    {
      icon: "ri-time-line",
      title: "Assuming 4–6 Weeks Is Enough",
      description: "Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service. Spring break and summer travel from Will County peaks March–August — plan ahead.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in the Joliet area or Will County." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx near Jefferson Street or Route 30 in Joliet." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier submits to the Chicago Passport Agency — just 45 minutes away but you never make the trip." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Joliet address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Joliet, IL?", a: "Fast Passport Center offers emergency passport processing in Joliet, Illinois in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (45 minutes north via I-55) — without you making the trip." },
    { q: "Does Joliet have its own passport agency?", a: "No — Joliet does not have a regional passport agency. The nearest facility is the Chicago Passport Agency (approximately 45 minutes north via I-55). Fast Passport Center's courier service means Joliet residents never need to make the trip." },
    { q: "Can I get an expedited passport in Plainfield, Romeoville, or New Lenox?", a: "Absolutely. Fast Passport Center serves all of Will County including Plainfield, Romeoville, Shorewood, Crest Hill, Lockport, New Lenox, Frankfort, Mokena, and all of the southwest Chicago suburbs. Our service works the same way everywhere." },
    { q: "What airport do Joliet travelers use for international flights?", a: "Chicago O'Hare International Airport (ORD) is the primary airport for Joliet and Will County travelers. O'Hare is approximately 45 minutes north via I-55 and I-290 and offers direct international flights worldwide. Chicago Midway (MDW) is approximately 40 minutes north via I-55. Always verify passport validity for at least 6 months past your return date." },
    { q: "How much does an expedited passport cost in Joliet, IL?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Where can I get passport photos in Joliet?", a: "Walgreens, CVS, and UPS Store locations throughout Joliet, Plainfield, and Romeoville offer passport photos. FedEx Office locations also provide them. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Does Fast Passport Center serve Joliet Junior College students?", a: "Yes. Fast Passport Center serves all of Will County including Joliet Junior College students and staff. We understand study abroad schedules, transfer program deadlines, and academic travel needs. Our courier service is especially valuable for students who can't take a day off to go to Chicago." },
    { q: "Can I get a passport for my child in Joliet?", a: "Yes. Fast Passport Center provides child passport services for minors under 16 throughout Joliet and Will County. Both parents or legal guardians must typically be present at the acceptance facility, and we guide you through the entire process." },
  ],
  photoLocations: [
    { name: "Walgreens — Joliet Jefferson", address: "2010 W Jefferson St, Joliet, IL 60435", hours: "Mon–Sun: 7AM–10PM" },
    { name: "UPS Store — Joliet", address: "2521 W Jefferson St, Joliet, IL 60435", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "CVS — Joliet Route 30", address: "2301 W Jefferson St, Joliet, IL 60435", hours: "Mon–Sun: 8AM–10PM" },
    { name: "FedEx Office — Joliet", address: "2775 Plainfield Rd, Joliet, IL 60435", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "Illinois SOS — Joliet", address: "2011 W Jefferson St, Joliet, IL 60435", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Illinois Secretary of State for driver's licenses, REAL ID, and vehicle registration. Online reservations strongly recommended.", featured: true },
    { name: "Illinois SOS — Plainfield", address: "14830 S Route 30, Plainfield, IL 60544", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Plainfield, Shorewood, and northern Joliet area. Full REAL ID processing available.", featured: false },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (IDPH)", url: "https://dph.illinois.gov/topics-services/birth-death-other-records.html", desc: "Illinois Department of Public Health — Division of Vital Records for Will County birth and death certificates" },
    { label: "Marriage Licenses (Will County Clerk)", url: "https://www.willcountyillinois.gov/county-clerk/", desc: "Will County Clerk — marriage license applications and certified copies for Joliet and surrounding suburbs" },
    { label: "Divorce Decrees (Will County Circuit Clerk)", url: "https://www.willcountyillinois.gov/circuit-clerk/", desc: "Will County Circuit Clerk — divorce decree records for Joliet and Will County residents" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Illinois birth, death, marriage, and divorce certificates online via VitalChek — fastest state-approved method" },
  ],
  airports: [
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "Approximately 45 minutes north via I-55 and I-290",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Primary international airport for Joliet and Will County. One of the world's busiest airports with direct flights to Europe, Asia, and the Americas. United and American Airlines hub.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "ORD Flight Info",
      featured: true,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "Approximately 40 minutes north via I-55",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Southwest Airlines hub with domestic connections. Good for travelers flying Southwest to connect to international destinations. Closer to Joliet than O'Hare.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "MDW Flight Info",
      featured: false,
    },
    {
      code: "JOT",
      name: "Joliet Regional Airport",
      distance: "Approximately 10 minutes west of downtown Joliet",
      address: "4000 W Jefferson St, Joliet, IL 60431",
      note: "Regional general aviation airport. Not a commercial hub, but used for private and charter flights.",
      flightUrl: "https://www.joliet.gov/",
      flightLabel: "JOT Info",
      featured: false,
    },
  ],
};